/**
 * Кольори іконки ДЗ. Індекс = homework_icon_color з бекенду
 * (EventResource / MonthResource): 1 сірий, 2 жовтий, 3 червоний, 4 зелений.
 * Значення = модифікатор класу .icon-- зі scss/parts/icon.scss.
 *
 * Використовується ще й у Programs (CourseProgramItem) — не перейменовувати.
 */
export const homeworkColors = {
  1: 'gray',
  2: 'yellow',
  3: 'red',
  4: 'green',
};

/** ДЗ виконане — єдиний стан, у якому іконка НЕ пульсує. */
export const HOMEWORK_DONE_COLOR = 4;

/**
 * Колір кільця пульсації (--pulse-color для @keyframes pulse* зі scss/components/program.scss).
 * Раніше сюди потрапляло значення з homeworkColors, тобто рядки 'yellow'/'red' — валідні
 * CSS-ключові слова, через що кільце було чистим #FFFF00 / #FF0000 замість палітри кабінету.
 */
export const homeworkPulseColors = {
  1: '#BBBBBB', // $gray-line
  2: '#FFC107', // $yellow-line
  3: '#FF6259', // $red-line
};

/**
 * Статус відвідування (event_lessons.attendance_status).
 *
 * ❗2 і 3 — РІЗНІ стани, стара мапа (константа visit) зливала їх в одне «не відвідано».
 * Звірено з CRM: кольори в `user/student-product-events.blade.php`
 * (1 → #afd2b8 зелений, 2 → #ffe7aa жовтий, 3 → #f8a2ad червоний) і списання заняття
 * в `CreateAttendanceDocuments` (`=== 1 || === 3`), тобто статус 2 НЕ списується —
 * саме тому під ним у CRM з'являється кнопка видати доступний урок.
 */
export const attendanceStates = {
  0: { key: 'none', label: 'Очікує відмітки' },
  1: { key: 'was', label: 'Відвідано' },
  2: { key: 'skip', label: 'Не враховується' },
  3: { key: 'miss', label: 'Пропущено' },
  4: { key: 'miss', label: 'Пропущено' },
};

/** Закреслюємо лише реально пропущене; «не враховується» — інший стан, не помилка студента. */
export const isMissedEvent = (event) => attendanceStates[event?.attendance_status]?.key === 'miss';

/**
 * Підсумок дня для стрічки з числом: коли всі події одного типу — пишемо тип один раз
 * («Заняття ×2»), інакше просто кількість. Це і наповнює порожню стрічку, і дозволяє
 * не повторювати слово в кожній події (у колонку ~110px воно поруч із часом не влазить).
 */
export const getDaySummary = (events = []) => {
  if (!events.length) return null;

  const keys = [...new Set(events.map((event) => event.type_key))];

  if (keys.length !== 1) {
    return { typeKey: null, label: `${events.length} ${events.length < 5 ? 'події' : 'подій'}` };
  }

  const label = events[0].type_label || '';

  return {
    typeKey: keys[0],
    label: events.length > 1 ? `${label} ×${events.length}` : label,
  };
};

/**
 * Стан події у часі. Заняття триває рівно годину (як і в решті розкладу).
 * Повертає 'past' | 'now' | 'future'.
 */
const getTimeState = (event) => {
  if (!event?.date) return 'future';

  // start_at — ISO зі зсувом Києва, тобто абсолютний момент: порівняння з now() правильне
  // в будь-якому поясі. Рядок date+hour парсився б у поясі браузера і для студента
  // за кордоном давав би «Триває зараз» на кілька годин раніше або пізніше.
  const start = event.start_at
    ? new Date(event.start_at)
    : new Date(`${String(event.date).slice(0, 10)}T${String(event.hour).padStart(2, '0')}:00:00`);

  if (Number.isNaN(start.getTime())) return 'future';

  const end = new Date(start.getTime() + 60 * 60 * 1000);
  const now = new Date();

  if (now < start) return 'future';
  if (now > end) return 'past';
  return 'now';
};

/** Підпис і колірний ключ статусу для картки в панелі подій. */
export const getEventState = (event) => {
  const timeState = getTimeState(event);

  if (timeState === 'now') return { key: 'now', label: 'Триває зараз' };
  if (timeState === 'future') return { key: 'plan', label: 'Заплановано' };

  return attendanceStates[event?.attendance_status] ?? attendanceStates[0];
};

/** Ключ дати 'yyyy-MM-dd' — і для події з API, і для клітинки календаря. */
export const toDateKey = (value) => {
  if (typeof value === 'string') return value.slice(0, 10);

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return toDateKey(new Date());

  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');

  return `${d.getFullYear()}-${month}-${day}`;
};
